// src/auth.ts
export type StoredAuth = {
  token: string;
  role: string;
};

const TOKEN_KEY_NEW = "access_token";
const TOKEN_KEY_OLD = "token";

const ROLE_KEY = "role";

function base64UrlDecode(input: string): string {
  // JWT uses base64url ( - _ ) and may omit padding
  const base64 = input.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
  return atob(padded);
}

export function decodeJwtPayload(token: string): any | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const json = base64UrlDecode(parts[1]);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function getAuth(): StoredAuth | null {
  const token = getToken();
  const role = localStorage.getItem(ROLE_KEY);
  if (!token || !role) return null;
  return { token, role };
}

export function setAuth(token: string, role: string) {
  localStorage.setItem(TOKEN_KEY_NEW, token);
  localStorage.removeItem(TOKEN_KEY_OLD); // keep clean
  localStorage.setItem(ROLE_KEY, role);
}

export function clearAuth() {
  localStorage.removeItem(TOKEN_KEY_NEW);
  localStorage.removeItem(TOKEN_KEY_OLD);
  localStorage.removeItem(ROLE_KEY);
}

export function getToken(): string | null {
  const tNew = localStorage.getItem(TOKEN_KEY_NEW);
  if (tNew) return tNew;

  const tOld = localStorage.getItem(TOKEN_KEY_OLD);
  if (tOld) {
    // migrate once
    localStorage.setItem(TOKEN_KEY_NEW, tOld);
    localStorage.removeItem(TOKEN_KEY_OLD);
    return tOld;
  }
  return null;
}

export function getRole(): string | null {
  return localStorage.getItem(ROLE_KEY);
}

// returns seconds since epoch, or null
export function getTokenExp(token: string): number | null {
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;
  return typeof exp === "number" ? exp : null;
}

export function isTokenExpired(token: string, skewSeconds = 15): boolean {
  const exp = getTokenExp(token);
  if (!exp) return true; // if token has no exp, treat as invalid
  const now = Math.floor(Date.now() / 1000);
  return now >= exp - skewSeconds;
}

/**
 * Schedules an auto-logout callback at token expiry.
 * Returns a cleanup fn.
 */
export function scheduleAutoLogout(token: string, onExpire: () => void) {
  const exp = getTokenExp(token);
  if (!exp) {
    // no exp -> immediate logout
    onExpire();
    return () => {};
  }

  const nowMs = Date.now();
  const expMs = exp * 1000;
  const delay = Math.max(0, expMs - nowMs);

  const timer = window.setTimeout(() => {
    onExpire();
  }, delay);

  return () => window.clearTimeout(timer);
}
